import type { QueryClient, UseQueryResult } from "@tanstack/react-query";
import type {
  ResolvedFeatureConfigUserView,
  ResolvedFeatureConfigAdminView,
  ResolvedFeatureConfigViewUnion,
} from "@shared/schemas/resolved-feature-config.schemas";
import type { ConfigContext } from "@shared/schemas/config-context.schemas";
import type { UpdateFeatureConfigInputType } from "@shared/schemas/feature-config-input.schemas";
import {
  ResolvedFeatureConfigUserViewSchema,
  ResolvedFeatureConfigAdminViewSchema,
  ResolvedFeatureConfigViewUnionSchema,
} from "@shared/schemas/resolved-feature-config.schemas";
import {
  EventFeaturePresetApplyResultSchema,
  type EventFeaturePresetApplyResult,
} from "@shared/schemas/event-feature-preset.schemas";
import type {
  EventFeaturePresetVariables,
  UnifiedUpdateFeatureConfigVariables,
  UnifiedResetFeatureConfigVariables,
} from "./feature-config.types";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import { applicationLogger } from "@frontend/shared/infrastructure/logger/application-logger.singleton";
import { createFeatureConfigApi } from "./feature-config.api";
import type { AxiosInstance } from "axios";
import { FEATURE_CONFIG_QUERY_KEYS } from "./feature-config-query-keys.constants";

export class FeatureConfigHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createFeatureConfigApi(apiClient);
  }

  createHooks() {
    const invalidateOnSuccess = (logContext: string) => () => {
      void this.invalidateResolveQueries().catch((err: Error) => {
        applicationLogger.error(
          `Failed to invalidate resolved feature-config queries after ${logContext}`,
          "FeatureConfigHooksHttpRepositoryImpl",
          err,
        );
      });
    };

    return {
      useGetFeatureConfigsUserView: (input: {
        readonly context: ConfigContext;
        readonly enabled?: boolean | undefined;
      }): UseQueryResult<readonly ResolvedFeatureConfigUserView[]> => {
        const { context, enabled = true } = input;
        return this.query({
          key: FEATURE_CONFIG_QUERY_KEYS.resolveList(context),
          fn: () => this.api.resolveConfigs({ context }),
          parse: (dtos) =>
            dtos.map((dto) => ResolvedFeatureConfigUserViewSchema.parse(dto)),
          enabled,
          staleTime: 30 * 1000,
          retry: 1,
        });
      },

      useGetFeatureConfigsAdminView: (input: {
        readonly context: ConfigContext;
        readonly enabled?: boolean | undefined;
      }): UseQueryResult<readonly ResolvedFeatureConfigAdminView[]> => {
        const { context, enabled = true } = input;
        return this.query({
          key: FEATURE_CONFIG_QUERY_KEYS.resolveList(context),
          fn: () => this.api.resolveConfigs({ context }),
          parse: (dtos) =>
            dtos.map((dto) => ResolvedFeatureConfigAdminViewSchema.parse(dto)),
          enabled,
          staleTime: 30 * 1000,
          retry: 1,
        });
      },

      useGetFeatureConfigByKeyUserView: (input: {
        readonly featureKey: string;
        readonly context: ConfigContext;
        readonly enabled?: boolean | undefined;
      }): UseQueryResult<ResolvedFeatureConfigUserView> => {
        const { featureKey, context, enabled = true } = input;
        return this.query({
          key: FEATURE_CONFIG_QUERY_KEYS.resolveByKey(featureKey, context),
          fn: () => this.api.resolveConfigByKey(featureKey, { context }),
          parse: (dto) => ResolvedFeatureConfigUserViewSchema.parse(dto),
          enabled: enabled && featureKey.length > 0,
          staleTime: 30 * 1000,
          retry: 1,
        });
      },

      useGetFeatureConfigByKeyAdminView: (input: {
        readonly featureKey: string;
        readonly context: ConfigContext;
        readonly enabled?: boolean | undefined;
      }): UseQueryResult<ResolvedFeatureConfigAdminView> => {
        const { featureKey, context, enabled = true } = input;
        return this.query({
          key: FEATURE_CONFIG_QUERY_KEYS.resolveByKey(featureKey, context),
          fn: () => this.api.resolveConfigByKey(featureKey, { context }),
          parse: (dto) => ResolvedFeatureConfigAdminViewSchema.parse(dto),
          enabled: enabled && featureKey.length > 0,
          staleTime: 30 * 1000,
          retry: 1,
        });
      },

      useUpdateFeatureConfig: () =>
        this.mutation<
          unknown,
          ResolvedFeatureConfigViewUnion,
          UnifiedUpdateFeatureConfigVariables
        >({
          fn: (vars) =>
            this.api.updateConfig(vars.featureKey, {
              scope: vars.scope,
              override: vars.override,
            }),
          parse: (dto) => ResolvedFeatureConfigViewUnionSchema.parse(dto),
          onSuccess: invalidateOnSuccess("mutation"),
        }),

      useUpdateFeatureConfigs: () =>
        this.mutation<
          readonly unknown[],
          readonly ResolvedFeatureConfigViewUnion[],
          {
            readonly scope: ConfigContext;
            readonly configs: ReadonlyArray<{
              readonly featureKey: string;
              readonly override: UpdateFeatureConfigInputType;
            }>;
          }
        >({
          fn: (vars) =>
            this.api.updateConfigs({
              scope: vars.scope,
              configs: vars.configs,
            }),
          parse: (dtos) =>
            dtos.map((dto) => ResolvedFeatureConfigViewUnionSchema.parse(dto)),
          onSuccess: invalidateOnSuccess("mutation"),
        }),

      useResetFeatureConfig: () =>
        this.mutation<
          unknown,
          ResolvedFeatureConfigViewUnion,
          UnifiedResetFeatureConfigVariables
        >({
          fn: (vars) => this.api.resetConfig(vars.featureKey, vars.scope),
          parse: (dto) => ResolvedFeatureConfigViewUnionSchema.parse(dto),
          onSuccess: invalidateOnSuccess("mutation"),
        }),

      useApplyEventPreset: () =>
        this.mutation<
          unknown,
          EventFeaturePresetApplyResult,
          EventFeaturePresetVariables
        >({
          fn: (vars) =>
            this.api.applyEventPreset(vars.eventIdPublic, vars.presetKey),
          parse: (dto) => EventFeaturePresetApplyResultSchema.parse(dto),
          onSuccess: invalidateOnSuccess("preset apply"),
        }),
    };
  }

  private invalidateResolveQueries(): Promise<void> {
    return this.queryClient.invalidateQueries({
      queryKey: ["feature-configs", "resolved"],
    });
  }
}
